import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { SupportLayout } from './SupportLayout';

export const CommunityPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'communityWaitlist'), {
        email: email.trim().toLowerCase(),
        source: 'community-page',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SupportLayout
      title="Host community"
      subtitle="A space for DARAK hosts to swap pricing tips, deal with tricky guests, and learn from each other."
    >
      <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 mb-10">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Coming soon</h2>
        <p className="text-gray-700 mb-6">
          We're building a private group for verified DARAK hosts. Early access goes out to hosts who sign up below.
        </p>

        {submitted ? (
          <div className="p-4 bg-white rounded-xl text-center">
            <p className="font-medium text-gray-900">You're on the list.</p>
            <p className="text-sm text-gray-600 mt-1">We'll email when the group opens.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Adding…' : 'Get early access'}
            </button>
          </form>
        )}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">What you'll find inside</h2>
      <ul className="space-y-3 text-gray-700">
        <li>
          <strong className="text-gray-900">Pricing benchmarks.</strong> What other hosts in Batroun, Faraya, Ehden,
          and Beirut charge by season.
        </li>
        <li>
          <strong className="text-gray-900">Guest red flags.</strong> Patterns that have caused damage or disputes,
          shared by hosts who've seen them.
        </li>
        <li>
          <strong className="text-gray-900">Cleaner and handyman recs.</strong> Vetted by hosts in your area.
        </li>
        <li>
          <strong className="text-gray-900">Direct line to the DARAK team.</strong> Product feedback and platform
          issues get triaged here.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-4">House rules for the community</h2>
      <ul className="space-y-3 text-gray-700">
        <li>Be civil. Disagreement is fine; insults aren't.</li>
        <li>No outside booking links. Don't poach guests off-platform.</li>
        <li>Don't share guest details that would identify them.</li>
        <li>Self-promo only in the threads marked for it.</li>
      </ul>
    </SupportLayout>
  );
};

export default CommunityPage;
